/* eslint-disable no-comments/disallowComments */
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  AuthHeaders,
  BaseApiService,
  CompanyDto,
  CoreInjectorTokens,
  Environment,
  GetManyDto,
  GetManyV2Dto,
  GetOneDto,
  PageableListV2Dto,
  ResponseDto,
} from '@fleet-beat/core';
import { Observable, catchError, map } from 'rxjs';
import {
  CompaniesSummaryDto,
  CompanyInvoice,
  CompanyOwnerDto,
  CompanySubscription,
  ExternalIntegration,
  ExternalIntegrationUpdateDto,
  StripePaymentMethod,
  TenantAuthResponseDto,
} from '../../models';

@Injectable({
  providedIn: 'root',
})
export class CompaniesApiService {
  private readonly baseUrl: string = `${this.environment.apiEndpoint}/companies`;
  private readonly baseAdminUrl: string = `${this.environment.apiEndpoint}/admin`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}

  getMany(params: GetManyDto, auth: AuthHeaders): Observable<CompanyDto[]> {
    return this.baseApiService.get(this.baseUrl, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanyDto[]>) => this.baseApiService.mapResponse(response)),
    );
  }

  getManyAsAdmin(params: GetManyDto, auth: AuthHeaders): Observable<PageableListV2Dto<CompanyDto>> {
    return this.baseApiService.get(`${this.baseAdminUrl}/companies`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanyDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<CompanyDto[]>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }

  getCompanies(params: GetManyDto, auth: AuthHeaders): Observable<PageableListV2Dto<CompanyDto>> {
    return this.baseApiService.get(`${this.baseUrl}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanyDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<CompanyDto[]>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }

  updateAsAdminById(companyId: string, data: CompanyDto, auth: AuthHeaders): Observable<ResponseDto<CompanyDto>> {
    return this.baseApiService.patch(`${this.baseAdminUrl}/companies/${companyId}`, { ...data }, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanyDto>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<CompanyDto>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }

  setSubscriptionPriceAsAdmin(
    companyId: string,
    data: { priceId: string },
    auth: AuthHeaders,
  ): Observable<ResponseDto<CompanyDto>> {
    return this.baseApiService
      .patch(`${this.baseAdminUrl}/companies/${companyId}/set-price`, { ...data }, { auth })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<CompanyDto>) => this.baseApiService.mapResponseV2(response)),
        map((response: ResponseDto<CompanyDto>) => {
          return {
            data: response.data,
            ...response.meta,
          };
        }),
      );
  }

  moveCompanyToTenantAsAdmin(id: string, tenantId: string, auth: AuthHeaders): Observable<ResponseDto<CompanyDto>> {
    return this.baseApiService.post(`${this.baseAdminUrl}/companies/${id}/to-tenant/${tenantId}`, {}, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanyDto>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  /* loginAsCompany(body: { companyId: string }, auth: AuthHeaders): Observable<ResponseDto<CompanyAuthResponseDto>> { // TO DELETE
    return this.baseApiService.post(`${this.baseAdminUrl}/auth/as-company`, { ...body }, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanyAuthResponseDto>) => this.baseApiService.mapResponseV2(response)),
    );
  } */

  loginAsTenant(body: { tenantId: string }, auth: AuthHeaders): Observable<ResponseDto<TenantAuthResponseDto>> {
    return this.baseApiService.post(`${this.baseAdminUrl}/auth/as-tenant`, { ...body }, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<TenantAuthResponseDto>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  getManyByIds(ids: string[], include: string, auth: AuthHeaders): Observable<CompanyDto[]> {
    return this.baseApiService.post(this.baseUrl, ids, { auth, params: { include } }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanyDto[]>) => this.baseApiService.mapResponse(response)),
    );
  }

  getOne(id: string, auth: AuthHeaders, params?: GetOneDto): Observable<CompanyDto> {
    return this.baseApiService.get(`${this.baseUrl}/${id}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanyDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  delete(id: string, auth: AuthHeaders): Observable<CompanyDto> {
    return this.baseApiService.delete(`${this.baseUrl}/${id}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanyDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  patchOne(id: string, data: CompanyDto, auth: AuthHeaders): Observable<CompanyDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanyDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  getTrackingSettings(id: string, auth: AuthHeaders): Observable<CompanyOwnerDto> {
    return this.baseApiService.get(`${this.baseUrl}/${id}/tracking-settings`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanyOwnerDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  setTrackingSettings(id: string, data: CompanyOwnerDto, auth: AuthHeaders): Observable<CompanyDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}/tracking-settings`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanyDto>) => this.baseApiService.mapResponse(response)),
    );
  }
  createCompany(data: CompanyDto, auth: AuthHeaders): Observable<boolean> {
    return this.baseApiService.post(`${this.baseUrl}`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<boolean>) => this.baseApiService.mapResponse(response)),
    );
  }
  createCompanyAsAdmin(data: CompanyDto, auth: AuthHeaders): Observable<boolean> {
    return this.baseApiService.post(`${this.baseAdminUrl}/companies`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<boolean>) => this.baseApiService.mapResponse(response)),
    );
  }
  setCompanyAsAdmin(companyId: string, userId: string, auth: AuthHeaders): Observable<boolean> {
    return this.baseApiService
      .post(`${this.baseAdminUrl}/companies/${companyId}/for-user/${userId}`, {}, { auth })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<boolean>) => this.baseApiService.mapResponse(response)),
      );
  }
  unsetCompanyAsAdmin(companyId: string, userId: string, auth: AuthHeaders): Observable<boolean> {
    return this.baseApiService.delete(`${this.baseAdminUrl}/companies/${companyId}/for-user/${userId}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<boolean>) => this.baseApiService.mapResponse(response)),
    );
  }
  setCompany(companyId: string, userId: string, auth: AuthHeaders): Observable<boolean> {
    return this.baseApiService.post(`${this.baseUrl}/${companyId}/for-user/${userId}`, {}, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<boolean>) => this.baseApiService.mapResponse(response)),
    );
  }
  unsetCompany(companyId: string, userId: string, auth: AuthHeaders): Observable<boolean> {
    return this.baseApiService.delete(`${this.baseUrl}/${companyId}/for-user/${userId}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<boolean>) => this.baseApiService.mapResponse(response)),
    );
  }

  // CARDS

  getCompanyCards(companyId: string, auth: AuthHeaders): Observable<PageableListV2Dto<StripePaymentMethod>> {
    return this.baseApiService.get(`${this.baseUrl}/${companyId}/cards`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<StripePaymentMethod[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<StripePaymentMethod[]>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }

  addCompanyCard(
    companyId: string,
    body: { source: string; default?: boolean },
    auth: AuthHeaders,
  ): Observable<StripePaymentMethod> {
    return this.baseApiService.post(`${this.baseUrl}/${companyId}/cards`, body, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<StripePaymentMethod>) => this.baseApiService.mapResponse(response)),
    );
  }

  setCompanyCardAsDefauld(companyId: string, id: string, auth: AuthHeaders): Observable<StripePaymentMethod> {
    return this.baseApiService.post(`${this.baseUrl}/${companyId}/cards/${id}`, {}, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<StripePaymentMethod>) => this.baseApiService.mapResponse(response)),
    );
  }

  deleteCompanyCard(companyId: string, id: string, auth: AuthHeaders): Observable<StripePaymentMethod> {
    return this.baseApiService
      .delete<StripePaymentMethod>(`${this.baseUrl}/${companyId}/cards/${id}`, { auth })
      .pipe(catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)));
  }

  // SUBSCRIPTIONS

  getCompanySubscription(companyId: string, auth: AuthHeaders): Observable<CompanySubscription> {
    return this.baseApiService.get(`${this.baseUrl}/${companyId}/subscriptions/current`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanySubscription>) => this.baseApiService.mapResponse(response)),
    );
  }

  setCompanySubscriptionTruckQuantity(
    companyId: string,
    body: { truckQuantity: number },
    auth: AuthHeaders,
  ): Observable<CompanySubscription> {
    return this.baseApiService.patch(`${this.baseUrl}/${companyId}/subscriptions/set-quantity`, body, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanySubscription>) => this.baseApiService.mapResponse(response)),
    );
  }

  createCompanySubscription(
    companyId: string,
    body: { truckQuantity: number },
    auth: AuthHeaders,
  ): Observable<CompanySubscription> {
    return this.baseApiService.post(`${this.baseUrl}/${companyId}/subscriptions`, body, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompanySubscription>) => this.baseApiService.mapResponse(response)),
    );
  }

  cancelCompanySubscription(companyId: string, auth: AuthHeaders): Observable<CompanySubscription> {
    return this.baseApiService
      .delete<CompanySubscription>(`${this.baseUrl}/${companyId}/subscriptions/cancel`, { auth })
      .pipe(catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)));
  }

  // EXTERNAL INTEGRATIONS

  getExternalIntegrations(companyId: string, auth: AuthHeaders): Observable<PageableListV2Dto<ExternalIntegration>> {
    return this.baseApiService.get(`${this.baseUrl}/${companyId}/external-integrations`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<ExternalIntegration[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<ExternalIntegration[]>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }

  getExternalIntegration(companyId: string, name: string, auth: AuthHeaders): Observable<ExternalIntegration> {
    return this.baseApiService.get(`${this.baseUrl}/${companyId}/external-integrations/${name}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<ExternalIntegration>) => this.baseApiService.mapResponse(response)),
    );
  }

  updateExternalIntegration(
    companyId: string,
    name: string,
    body: ExternalIntegrationUpdateDto,
    auth: AuthHeaders,
  ): Observable<ExternalIntegration> {
    return this.baseApiService.put(`${this.baseUrl}/${companyId}/external-integrations/${name}`, body, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<ExternalIntegration>) => this.baseApiService.mapResponse(response)),
    );
  }

  deleteExternalIntegration(companyId: string, name: string, auth: AuthHeaders): Observable<ExternalIntegration> {
    return this.baseApiService.delete(`${this.baseUrl}/${companyId}/external-integrations/${name}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<ExternalIntegration>) => this.baseApiService.mapResponse(response)),
    );
  }

  // INVOICES

  getCompanyInvoices(
    companyId: string,
    params: GetManyV2Dto,
    auth: AuthHeaders,
  ): Observable<ResponseDto<CompanyInvoice[]>> {
    return this.baseApiService
      .get(`${this.baseUrl}/${companyId}/invoices`, { auth, params })
      .pipe(catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)));
  }

  getCompaniesSummaryAsAdmin(auth: AuthHeaders): Observable<CompaniesSummaryDto> {
    return this.baseApiService.get(`${this.baseAdminUrl}/companies/summary`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CompaniesSummaryDto>) => this.baseApiService.mapResponse(response)),
    );
  }
}
